/**
 * As peças de cada secção da sidebar: o item, o acordeão de sub-grupo e o grupo
 * da secção. Separadas do `AppSidebar` (o esqueleto: marca, pesquisa, lista de
 * secções) para cada ficheiro ter uma só razão para mudar.
 */

import { Link } from "react-router-dom";
import { useState, useMemo, useCallback, useEffect } from "react";
import { ChevronRight, LayoutDashboard, type LucideIcon } from "lucide-react";
import { iconByName } from "@/lib/icons";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useClientProfile } from "@/contexts/client-profile-context";
import { usePendingInvitesCount } from "@/hooks/hr/calendar/use-calendar-events";
import { useMenuBadge } from "@/hooks/layout/use-menu-badge";
import { CALENDAR_MODULES } from "@/lib/menu-sections";
import { isMenuSubGroup, MENU_SECTIONS, type MenuPath, type MenuSection, type MenuSubGroup } from "@/lib/menu-paths";

const EXACT_MATCH_PATHS = new Set([
  "/dashboard",
  "/dashboard/settings",
  // "/dashboard/hr/calendar" (Mapa de Férias) is a prefix of the new
  // /dashboard/hr/calendar/{individual,team,company,...} routes. Force exact
  // match so it doesn't light up when the user is on another calendar page.
  "/dashboard/hr/calendar",
]);

const SUB_GROUP_ICONS: Record<string, string> = {
  "Talento":            "Users2",
  "Desenvolvimento":    "GraduationCap",
  "Mobilidade Interna": "ArrowRightLeft",
  "Ambiente":           "Leaf",
};

function getIcon(iconName?: string): LucideIcon | null {
  if (!iconName) return null;
  return iconByName(iconName) ?? LayoutDashboard;
}

export function getSectionIdForPath(pathname: string): string | null {
  for (const section of MENU_SECTIONS) {
    const items = section.items.flatMap((item) =>
      isMenuSubGroup(item) ? item.items : [item]
    );
    if (items.some((item) => isPathActive(pathname, item.path))) {
      return section.id;
    }
  }
  return null;
}

function getActiveSubGroupLabels(pathname: string, items: Array<MenuPath | MenuSubGroup>): Set<string> {
  const active = new Set<string>();
  for (const item of items) {
    if (isMenuSubGroup(item)) {
      if (item.items.some((si) => isPathActive(pathname, si.path))) {
        active.add(item.groupLabel);
      }
    }
  }
  return active;
}

export function isPathActive(pathname: string, itemPath: string): boolean {
  if (EXACT_MATCH_PATHS.has(itemPath)) return pathname === itemPath;
  return pathname === itemPath || pathname.startsWith(itemPath + "/");
}

// ── Nav Item ─────────────────────────────────────────────────────────────────

/** O selo dos convites: um número ao lado do nome, ou um ponto sobre o ícone quando a sidebar está colapsada. */
function CountBadge({ count, collapsed, label }: { count: number; collapsed: boolean; label: string }) {
  if (!count) return null;
  if (collapsed) return <span aria-hidden title={label} className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background" />;
  return (
    <span aria-hidden title={label} className="ml-auto flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold tabular-nums text-primary-foreground">
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function NavItem({ menu, isActive, collapsed, onClick }: {
  menu: MenuPath;
  isActive: boolean;
  collapsed: boolean;
  onClick?: () => void;
}) {
  const Icon = getIcon(menu.icon);
  const isCalendar = menu.path === "/dashboard/calendar";
  // Só pergunta pelos convites se este item é o Calendário E o perfil tem um módulo que o alimente (senão a rota dá 404).
  const { hasModule } = useClientProfile();
  const { data: pendingInvites = 0 } = usePendingInvitesCount(isCalendar && CALENDAR_MODULES.some((m) => hasModule(m)));
  const invitesLabel = pendingInvites === 1 ? "1 convite por responder" : `${pendingInvites} convites por responder`;
  // O trabalho à espera (ocorrências abertas, pedidos por resolver): o número ao lado do nome, como nos convites.
  const trabalho = useMenuBadge(menu.path);
  const rotulo = isCalendar ? (pendingInvites > 0 ? `${menu.label}, ${invitesLabel}` : undefined) : trabalho && trabalho.count > 0 ? `${menu.label}, ${trabalho.label}` : undefined;

  const link = (
    <Link
      to={menu.path}
      onClick={onClick}
      aria-label={rotulo}
      className={cn(
        "group relative flex items-center rounded-lg text-[13px] transition-colors duration-100",
        collapsed ? "h-9 w-9 justify-center" : "h-9 gap-2.5 px-2.5",
        isActive
          ? "bg-foreground/6 font-medium text-foreground"
          : "font-normal text-muted-foreground hover:bg-foreground/[0.035] hover:text-foreground"
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            "h-[17px] w-[17px] shrink-0 transition-colors",
            isActive ? "text-foreground" : "text-muted-foreground/70 group-hover:text-foreground"
          )}
          strokeWidth={1.75}
        />
      )}
      {!collapsed && <span className="truncate leading-none">{menu.label}</span>}
      {isCalendar && <CountBadge count={pendingInvites} collapsed={collapsed} label={invitesLabel} />}
      {trabalho && <CountBadge count={trabalho.count} collapsed={collapsed} label={trabalho.label} />}
    </Link>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>
        {menu.label}
      </TooltipContent>
    </Tooltip>
  );
}

// ── Sub-group accordion ───────────────────────────────────────────────────────

function SubGroupAccordion({ group, isExpanded, onToggle, isActive, onItemClick }: {
  group: MenuSubGroup;
  isExpanded: boolean;
  onToggle: () => void;
  isActive: (path: string) => boolean;
  onItemClick?: () => void;
}) {
  const Icon = getIcon(SUB_GROUP_ICONS[group.groupLabel]);
  const hasActive = group.items.some((si) => isActive(si.path));

  return (
    <div>
      <button
        onClick={onToggle}
        className={cn(
          "flex h-8 w-full items-center gap-2 rounded-lg px-2.5 text-[13px] transition-colors duration-100",
          hasActive
            ? "font-medium text-foreground"
            : "font-normal text-muted-foreground hover:bg-foreground/[0.035] hover:text-foreground"
        )}
      >
        {Icon && (
          <Icon
            className={cn(
              "h-[17px] w-[17px] shrink-0",
              hasActive ? "text-foreground" : "text-muted-foreground/70"
            )}
            strokeWidth={1.75}
          />
        )}
        <span className="flex-1 truncate text-left leading-none">{group.groupLabel}</span>
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-200",
            isExpanded && "rotate-90"
          )}
        />
      </button>

      <div
        className={cn(
          "overflow-hidden transition-all duration-200 ease-in-out",
          isExpanded ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        {/* A guia vertical liga visualmente os filhos ao pai — sem ela, um
            sub-menu aberto lê-se como mais uma lista solta. */}
        <div className="ml-[18px] space-y-px border-l border-border/60 pl-2 py-0.5">
          {group.items.map((si) => (
            <NavItem
              key={si.path}
              menu={si}
              isActive={isActive(si.path)}
              collapsed={false}
              onClick={onItemClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Section group ─────────────────────────────────────────────────────────────

export function SectionGroup({ section, items, isExpanded, onToggle, isActive, onItemClick, pathname, collapsed }: {
  section: MenuSection;
  items: Array<MenuPath | MenuSubGroup>;
  isExpanded: boolean;
  onToggle: () => void;
  isActive: (path: string) => boolean;
  onItemClick?: () => void;
  pathname: string;
  collapsed: boolean;
}) {
  const flatItems = items.flatMap((item) => (isMenuSubGroup(item) ? item.items : [item]));
  const hasActiveItem = flatItems.some((item) => isActive(item.path));

  const initialOpen = useMemo(() => getActiveSubGroupLabels(pathname, items), [pathname, items]);
  const [openSubGroups, setOpenSubGroups] = useState<Set<string>>(initialOpen);

  useEffect(() => {
    const nowActive = getActiveSubGroupLabels(pathname, items);
    if (nowActive.size > 0) {
      setOpenSubGroups((prev) => {
        const next = new Set(prev);
        nowActive.forEach((label) => next.add(label));
        return next;
      });
    }
  }, [pathname, items]);

  const toggleSubGroup = useCallback((label: string) => {
    setOpenSubGroups((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  }, []);

  // Colapsada, os títulos de secção e os acordeões não têm onde caber: fica a
  // lista plana de ícones, que é o que o modo ícone é.
  if (collapsed) {
    return (
      <div className="space-y-0.5">
        {flatItems.map((item) => (
          <NavItem
            key={item.path}
            menu={item}
            isActive={isActive(item.path)}
            collapsed
            onClick={onItemClick}
          />
        ))}
      </div>
    );
  }

  // Uma secção `standalone` (o topo) são ligações directas, sem "pai" — um NavItem por item.
  if (section.standalone) {
    return (
      <div className="space-y-px">
        {flatItems.map((item) => (
          <NavItem key={item.path} menu={item} isActive={isActive(item.path)} collapsed={false} onClick={onItemClick} />
        ))}
      </div>
    );
  }

  // Single-item section — render flat, no collapsible wrapper
  if (flatItems.length === 1) {
    return (
      <NavItem
        menu={flatItems[0]}
        isActive={isActive(flatItems[0].path)}
        collapsed={false}
        onClick={onItemClick}
      />
    );
  }

  return (
    <div>
      <button
        onClick={onToggle}
        className={cn(
          "group flex h-7 w-full items-center gap-1 rounded-lg px-2.5 transition-colors duration-100",
          hasActiveItem
            ? "text-muted-foreground"
            : "text-muted-foreground/60 hover:text-muted-foreground"
        )}
      >
        <ChevronRight
          className={cn(
            "h-3 w-3 shrink-0 text-muted-foreground/40 transition-transform duration-200",
            isExpanded && "rotate-90"
          )}
        />
        <span className="flex-1 text-left text-[11px] font-medium uppercase tracking-widest">
          {section.title}
        </span>
      </button>

      <div
        className={cn(
          "overflow-hidden transition-all duration-200 ease-in-out",
          isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="space-y-px pb-1">
          {items.map((item) =>
            isMenuSubGroup(item) ? (
              <SubGroupAccordion
                key={item.groupLabel}
                group={item}
                isExpanded={openSubGroups.has(item.groupLabel)}
                onToggle={() => toggleSubGroup(item.groupLabel)}
                isActive={isActive}
                onItemClick={onItemClick}
              />
            ) : (
              <NavItem
                key={item.path}
                menu={item}
                isActive={isActive(item.path)}
                collapsed={false}
                onClick={onItemClick}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
}
